import * as React from 'react';

import {colors} from '../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {NutshellLogoMarkIcon} from '../icon/svg/nutshell-logo-mark-icon';
import {Stack} from '../layout/stack/stack';
import {RichTextDisplay} from '../rich-text-display/rich-text-display';
import {Body} from '../typography/body';

import {AppearanceContent} from './appearance-content';
import {BookingContent} from './booking-content';
import {ChatActionBar} from './chat-action-bar';
import {ChatFooter} from './chat-footer';
import {ChatHeader} from './chat-header';
import {ChatToggleButton} from './chat-toggle-button';
import {ChatbotContent} from './chatbot-content';
import {EngagementFormContent} from './engagement-form-content';
import {FormContent} from './form-content';
import {OfflineContent} from './offline-content';
import {OnlineContent} from './online-content';
import type {
    FormProps,
    OfflineProps,
    OnlineProps,
    ChatbotProps,
    AppearanceProps,
    EngagementFormProps,
    BookingProps,
} from './types';

import styles from './chat-preview.module.css';

type Props =
    | OnlineProps
    | OfflineProps
    | FormProps
    | ChatbotProps
    | AppearanceProps
    | EngagementFormProps
    | BookingProps;

export const ChatPreview = (props: Props) => {
    const {
        headerTitle,
        subheader,
        icon,
        backgroundColor,
        showNudge,
        nudgeText,
        showDisclaimer,
        disclaimerText,
    } = props;

    let content = null;
    switch (props.status) {
        case 'online':
            content = (
                <OnlineContent
                    conversationStarter={props.conversationStarter}
                    intakeFormSettings={props.intakeFormSettings}
                />
            );
            break;
        case 'offline':
            content = <OfflineContent offlineMessage={props.offlineMessage} />;
            break;
        case 'form':
            content = (
                <FormContent
                    selectedFields={props.selectedFields}
                    automatedMessage={props.automatedMessage}
                    hasEnabledConversationalForms={props.hasEnabledConversationalForms}
                />
            );
            break;
        case 'chatbot':
            content = (
                <ChatbotContent name={props.name} tone={props.tone} icon={props.chatbotIcon} />
            );
            break;
        case 'appearance':
            content = (
                <AppearanceContent
                    conversationStarter={props.conversationStarter}
                    secondaryColor={props.secondaryColor}
                    chatbotName={props.chatbotName}
                    chatbotIcon={props.chatbotIcon}
                    suggestions={props.suggestions}
                    isLoadingSuggestions={props.isLoadingSuggestions}
                />
            );
            break;
        case 'engagement-form':
            content = (
                <EngagementFormContent
                    formType={props.formType}
                    formDescription={props.formDescription}
                    formIcon={props.formIcon}
                    shouldLinkItem={props.shouldLinkItem}
                    linkItem={props.linkItem}
                />
            );
            break;
        case 'booking':
            content = <BookingContent />;
            break;
        default:
            content = null;
    }

    const isOnline = props.status === 'form' ? props.isOnline : undefined;

    return (
        <div className='flex justify-center'>
            <Stack spacing={16} alignItems='end'>
                <div className={styles['chat-preview-container']}>
                    <ChatHeader
                        backgroundColor={backgroundColor}
                        headerTitle={headerTitle}
                        subheader={subheader}
                        status={props.status}
                        icon={icon}
                        isOnline={isOnline}
                    />
                    <div className={styles['chat-body']}>
                        {showDisclaimer && disclaimerText && (
                            <div>
                                <RichTextDisplay
                                    html={disclaimerText}
                                    size='mini'
                                    color='light'
                                    centerContent={true}
                                />
                            </div>
                        )}
                        {content}
                    </div>
                    <ChatFooter
                        showPoweredBy={props.showPoweredBy}
                        shouldHideInputField={Boolean(props.shouldHideInputField)}
                        showSubmitButton={props.showSubmitButton}
                        submitButtonText={props.submitButtonText}
                    />
                    {props.showPoweredBy && (
                        <div className='flex gap-8 justify-center align-center pb-8 background-white br-bl-3 br-br-3'>
                            <NutshellLogoMarkIcon
                                size={11}
                                fill={colors.orange}
                                wrapWithDiv={false}
                            />
                            <Body size='mini' color='light'>
                                Powered by Nutshell
                            </Body>
                        </div>
                    )}
                </div>
                {props.actionBarEnabled && props.actions && props.secondaryColor ? (
                    <ChatActionBar
                        shownActions={props.actionBarShownActions || []}
                        actionsDetails={props.actions}
                        backgroundColor={backgroundColor}
                        isOpen={true}
                        shouldSelectAction={props.shouldSelectAction}
                    />
                ) : (
                    <ChatToggleButton
                        backgroundColor={backgroundColor}
                        showNudge={showNudge}
                        nudgeText={nudgeText}
                        isChatOpen={true}
                        openIcon={icon}
                    />
                )}
            </Stack>
        </div>
    );
};
